import BottomBar from './BottomBar';

import { useEffect, useState } from 'react';
import { getFormActionButtons, getFormTitle, transformFormData } from '../../utils';

import { AritoFormV2, HeaderTabsV2 } from '@/components/custom/erp';
import { useFormFieldState } from '../../hooks/useFormFieldState';
import { useRepeatRowsHandler } from '@/lib/table-handlers';
import { useAuth } from '@/contexts/auth-context';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { formSchema } from '../../schema';
import { FormMode } from '@/types/form';

import type { PhieuNhapDieuChuyen, PhieuNhapDieuChuyenFormData } from '@/types/schemas';
import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: PhieuNhapDieuChuyen | null;
  onSubmit?: (data: PhieuNhapDieuChuyenFormData) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const {
    rows: detailRows,
    setRows: setDetailRows,
    selectedRowUuid: detailSelectedRowUuid,
    handleRowClick: detailHandleRowClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange
  } = useDetailRows(initialData?.chi_tiet_data || []);
  const { state, actions } = useFormFieldState(initialData);

  console.log(initialData);

  const handleRepeatDetailRows = useRepeatRowsHandler({
    setRows: setDetailRows
  });

  useEffect(() => {
    if (!open) {
      setIsFormDirty(false);
      setActiveTab('info');
    }
  }, [open]);

  const handleSubmit = (data: PhieuNhapDieuChuyenFormData) => {
    const formData = transformFormData(data, state, detailRows, entityUnit);

    onSubmit?.({ ...formData });
    setIsFormDirty(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={
          (initialData ? { ...initialData, ma_nt: initialData.ma_nt_data?.uuid } : initialData) as
            | Partial<PhieuNhapDieuChuyenFormData>
            | undefined
        }
        title={title}
        actionButtons={actionButtons}
        subTitle='Phiếu nhập điều chuyển'
        onSubmit={handleSubmit}
        onClose={handleClose}
        schema={formSchema}
        headerFields={
          <div onChange={() => setIsFormDirty(true)}>
            <HeaderTabsV2
              tabs={[
                {
                  id: 'info',
                  label: 'Thông tin',
                  component: <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
                },
                ...(formMode === 'view'
                  ? [
                      {
                        id: 'history',
                        label: 'Lịch sử',
                        component: <HistoryTab />
                      }
                    ]
                  : [])
              ]}
              actionButtons={
                formMode !== 'add' && (
                  <InfoDropdown uuid={initialData?.uuid || ''} ma_ct={initialData?.chung_tu || undefined} />
                )
              }
              onTabChange={handleTabChange}
              defaultTabIndex={activeTab === 'info' ? 0 : activeTab === 'history' ? 1 : 0}
            />
          </div>
        }
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  handleExport={() => {}}
                  handlePin={() => {}}
                  onRepeatForRows={handleRepeatDetailRows}
                />
              )
            }
          ]
        }
        bottomBar={activeTab === 'info' && <BottomBar totals={{ totalQuantity: 0, totalAmount: 0 }} />}
      />
    </>
  );
};

export { FormDialog };
